import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _IconErrorOutlined, _CircularProgress, _IconClose;
const _excluded = ["variant", "isLoading", "onChange", "InputProps"];
import PropTypes from 'prop-types';
import React from 'react';
import IconButton from '../IconButton';
import TextField from '../TextField';
import { IconClose, IconErrorOutlined, IconSearch } from '../icons';
import { CircularProgress, InputAdornment } from '../indexMaterial';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 *
 * Demos:
 *
 * - [TextField](https://ui-packages.isbuildingluma.com/ui-core/react-text-field/)
 *
 * API:
 *
 * - [TextFieldSearch API](https://ui-packages.isbuildingluma.com/ui-core/api/text-field-search/)
 * - inherits [TextField API](https://mui.com/material-ui/api/text-field/)
 */
const TextFieldSearch = /*#__PURE__*/React.forwardRef(function TextFieldSearch(_ref, ref) {
  let {
      variant,
      isLoading,
      onChange,
      InputProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(TextField, _extends({
    ref: ref,
    onChange: event => onChange(event.target.value),
    rounded: variant === 'primary',
    InputProps: _extends({}, InputProps, {
      startAdornment: !(InputProps != null && InputProps.readOnly) && /*#__PURE__*/_jsx(InputAdornment, {
        position: "start",
        children: rest.error && !rest.disabled ? _IconErrorOutlined || (_IconErrorOutlined = /*#__PURE__*/_jsx(IconErrorOutlined, {
          fontSize: "small",
          color: "error"
        })) : /*#__PURE__*/_jsx(IconSearch, {
          fontSize: variant === 'primary' ? undefined : 'small',
          color: variant === 'primary' ? 'disabled' : undefined
        })
      }),
      // NOTE: Use `type="search"` instead of custom adornment for the `x` button?
      endAdornment: /*#__PURE__*/_jsxs(React.Fragment, {
        children: [!rest.disabled && !(InputProps != null && InputProps.readOnly) && /*#__PURE__*/_jsxs(InputAdornment, {
          position: "end",
          children: [isLoading && (_CircularProgress || (_CircularProgress = /*#__PURE__*/_jsx(CircularProgress, {
            size: 20
          }))), !!rest.value && /*#__PURE__*/_jsx(IconButton, {
            sx: theme => ({
              ':focus-visible': {
                outline: `1px solid ${theme.palette.tertiary.light}`,
                bgcolor: theme.palette.background.default
              }
            }),
            "aria-label": "Clear",
            color: rest.error ? 'error' : 'primary',
            onClick: () => onChange(''),
            children: _IconClose || (_IconClose = /*#__PURE__*/_jsx(IconClose, {
              fontSize: "small"
            }))
          })]
        }), InputProps == null ? void 0 : InputProps.endAdornment]
      })
    })
  }, rest));
});
process.env.NODE_ENV !== "production" ? TextFieldSearch.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "TextFieldSearchProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the label is displayed in an error state.
   * @default false
   */
  error: PropTypes.bool,
  /**
   * Props applied to the Input element.
   * It will be a [`FilledInput`](/material-ui/api/filled-input/),
   * [`OutlinedInput`](/material-ui/api/outlined-input/) or [`Input`](/material-ui/api/input/)
   * component depending on the `variant` prop value.
   */
  InputProps: PropTypes.object,
  /**
   * If `true`, it'll show the loading spinner
   */
  isLoading: PropTypes.bool,
  /**
   * Callback fired when the value is changed.
   *
   * @param {string} value The new value
   */
  onChange: PropTypes.func.isRequired,
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * The variant of the component
   * @default 'default'
   */
  variant: PropTypes.oneOf(['default', 'primary'])
} : void 0;
export default TextFieldSearch;